// pages/public/ProjectMapViewer.tsx
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps';
import MapCameraController from 'components/common/MapCameraController';
import MapLegend from 'components/common/MapLegend';
import SignalOverlayViewModal from 'components/video/SignalOverlayViewModal';
import { useOverlaySigns } from 'hooks/useOverlaySigns';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { ImageMarker, videoProjectService } from 'services/videoService';
import { getServiceImageStyle, XOverlay } from 'utils/signImageFilters';

interface Project {
  id: number;
  name: string;
  description?: string;
}

interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  type: 'sign' | 'current' | 'image';
  signData?: ImageMarker;
  projectName?: string;
  projectId?: number;
  isOverlay?: boolean;
  serviceId?: number;
  imageUrl?: string;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const DEFAULT_CENTER = { lat: -27.5954, lng: -48.5480 };
const DEFAULT_ZOOM = 12;

const ProjectMapViewer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [mainProjectSigns, setMainProjectSigns] = useState<ImageMarker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedSignMarkerId, setSelectedSignMarkerId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);

  const projectIds = useMemo(() => {
    const ids: number[] = [];
    if (projectId) ids.push(Number(projectId));
    
    const projectsParam = searchParams.get('projects');
    if (projectsParam) {
      const additionalIds = projectsParam
        .split(',')
        .map(id => Number(id.trim()))
        .filter(id => !isNaN(id) && !ids.includes(id));
      ids.push(...additionalIds);
    }
    return ids;
  }, [projectId, searchParams]);

  const mainProjectId = projectIds[0] || 0;
  const overlayProjectIds = projectIds.slice(1);

  const {
    overlaySigns,
    
  } = useOverlaySigns({
    currentProjectId: mainProjectId,
    selectedProjectIds: overlayProjectIds,
    enabled: overlayProjectIds.length > 0
  });

  useEffect(() => {
    const loadProjectsData = async () => {
      if (projectIds.length === 0) {
        setError('Nenhum projeto especificado na URL');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const allProjects = await videoProjectService.getAll();
        const selectedProjects = allProjects.filter(p => projectIds.includes(p.id));

        if (selectedProjects.length === 0) {
          setError('Projeto(s) não encontrado(s)');
          setLoading(false);
          return;
        }

        setProjects(selectedProjects);

        if (mainProjectId) {
          const [devices, supports, services, films] = await Promise.all([
            videoProjectService.getSignalDevices(),
            videoProjectService.getAllSignalSupports(),
            videoProjectService.getSignalServices(),
            videoProjectService.getSignalFilms()
          ]);

          const markers = await videoProjectService.getProjectMarkers(mainProjectId);
          
          const enrichedMarkers = markers.map(marker => {
            const device = marker.signal_device?.symbol_name
              ? marker.signal_device
              : devices.find(d => d.id === marker.signal_device_id);

            const support = marker.signal_support?.code
              ? marker.signal_support
              : supports.find(s => s.id === marker.signal_support_id);

            const service = marker.signal_service?.name
              ? marker.signal_service
              : services.find(s => s.id === marker.service_id);

            const film = marker.signal_film?.name
              ? marker.signal_film
              : films.find(f => f.id === marker.signal_film_id);

            return {
              ...marker,
              signal_device: device,
              signal_support: support,
              signal_service: service,
              signal_film: film
            };
          });

          setMainProjectSigns(enrichedMarkers);

          if (enrichedMarkers.length > 0) {
            const firstSign = enrichedMarkers[0];
            setMapCenter({ lat: firstSign.lat, lng: firstSign.long });
            setMapZoom(15);
          }
        }
      } catch (err) {
        console.error('[ProjectMapViewer] Erro:', err);
        setError('Erro ao carregar dados dos projetos');
      } finally {
        setLoading(false);
      }
    };

    loadProjectsData();
  }, [projectIds, mainProjectId]);

  const mapMarkers: MapMarker[] = useMemo(() => {
    const markers: MapMarker[] = [];

    const getServiceName = (serviceId: number) => {
      switch (serviceId) {
        case 1: return 'Implantação';
        case 2: return 'Remoção';
        case 3: return 'Limpeza';
        case 4: return 'Manter';
        default: return 'Serviço';
      }
    };

    mainProjectSigns.forEach(sign => {
      const serviceName = getServiceName(sign.service_id);
      const code = sign.signal_device?.code || 'Placa';
      const name = sign.signal_device?.name || '';
      const title = `${serviceName} - ${code}${name ? ` - ${name}` : ''}`;

      markers.push({
        id: `main-${sign.id}`,
        lat: sign.lat,
        lng: sign.long,
        title,
        type: 'sign',
        signData: sign,
        projectId: mainProjectId,
        projectName: projects.find(p => p.id === mainProjectId)?.name,
        isOverlay: false,
        serviceId: sign.service_id,
        imageUrl: sign.signal_device?.symbol_name 
          ? `/images/signs/${sign.signal_device.symbol_name}`
          : undefined
      });
    });

    overlaySigns.forEach(sign => {
      const serviceName = getServiceName(sign.service_id);
      const code = sign.signal_device?.code || 'Placa';
      const name = sign.signal_device?.name || '';
      const title = `${serviceName} - ${code}${name ? ` - ${name}` : ''}`;

      markers.push({
        id: `overlay-${sign.id}`,
        lat: sign.lat,
        lng: sign.long,
        title,
        type: 'sign',
        signData: sign,
        projectId: sign.projectId,
        projectName: sign.projectName,
        isOverlay: true,
        serviceId: sign.service_id,
        imageUrl: sign.signal_device?.symbol_name 
          ? `/images/signs/${sign.signal_device.symbol_name}`
          : undefined
      });
    });

    return markers;
  }, [mainProjectSigns, overlaySigns, projects, mainProjectId]);

  const selectedSign = useMemo(() => {
    if (!selectedSignMarkerId) return null;
    const marker = mapMarkers.find(m => m.id === selectedSignMarkerId);
    if (!marker?.signData) return null;
    
    // ✅ Adicionar projectId ao sign
    return {
      ...marker.signData,
      projectId: marker.projectId
    };
  }, [selectedSignMarkerId, mapMarkers]);

  const handleMarkerClick = (markerId: string) => {
    setSelectedSignMarkerId(markerId);
  };

  const handleCloseModal = () => {
    setSelectedSignMarkerId(null);
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Carregando projeto...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Erro ao carregar projeto</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1001,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderBottom: '1px solid #dee2e6',
          padding: '12px 20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
              {projects[0]?.name || 'Visualização de Projeto'}
            </h5>
            {projects[0]?.description && (
              <p style={{ margin: 0, fontSize: '12px', color: '#6c757d' }}>
                {projects[0].description}
              </p>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {mainProjectSigns.length + overlaySigns.length} sinalizações
            {projects.length > 1 && ` · ${projects.length} projetos`}
          </div>
        </div>
      </div>

      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          defaultCenter={mapCenter}
          defaultZoom={mapZoom}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapId="project-viewer-map"
          style={{ width: '100%', height: '100%', paddingTop: '60px' }}
        >
          <MapCameraController
            markers={mapMarkers}
            selectedSignMarkerId={selectedSignMarkerId}
            autoZoom={true}
            targetZoom={17}
            smoothPan={true}
          />

          {mapMarkers.map(marker => (
            <CustomMarker
              key={marker.id}
              marker={marker}
              isSelected={marker.id === selectedSignMarkerId}
              onClick={() => handleMarkerClick(marker.id)}
            />
          ))}
        </Map>
      </APIProvider>

      <MapLegend
        signMarkersCount={mainProjectSigns.length}
        overlayMarkersCount={overlaySigns.length}
        showSignImages={true}
        onToggleSignImages={undefined}
        className="map-legend"
        defaultCollapsed={false}
      />

      {selectedSign && (
        <SignalOverlayViewModal
          show={!!selectedSign}
          onHide={handleCloseModal}
          sign={selectedSign}
          hideProjectButton={true}
        />
      )}
    </div>
  );
};

interface CustomMarkerProps {
  marker: MapMarker;
  isSelected: boolean;
  onClick: () => void;
}

const CustomMarker: React.FC<CustomMarkerProps> = ({ marker, isSelected, onClick }) => {
const isOverlay: boolean = marker.isOverlay || false;  // ✅ Adicionar : boolean
  const imageStyle = getServiceImageStyle(marker.serviceId, isOverlay);
  const baseSize = isSelected ? 52 : 40;
  const imageSize = isSelected ? 48 : 36;

  return (
    <AdvancedMarker
      position={{ lat: marker.lat, lng: marker.lng }}
      onClick={onClick}
      title={marker.title}
    >
      <div
        style={{
          position: 'relative',
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            border: imageStyle.borderColor 
              ? `${imageStyle.borderWidth || '3px'} solid ${imageStyle.borderColor}`
              : '3px solid white',
            boxShadow: isSelected 
              ? '0 4px 12px rgba(0,0,0,0.4)'
              : '0 2px 6px rgba(0,0,0,0.3)',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {marker.imageUrl ? (
            <img
              src={marker.imageUrl}
              alt={marker.title}
              style={{
                width: `${imageSize}px`,
                height: `${imageSize}px`,
                objectFit: 'contain',
                filter: imageStyle.filter,
                opacity: imageStyle.opacity || 1
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div style={{ fontSize: '24px' }}>🚦</div>
          )}
        </div>
        {imageStyle.showXOverlay && <XOverlay size={baseSize * 0.8} />}
      </div>
    </AdvancedMarker>
  );
};

export default ProjectMapViewer;